import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Trash2, Check, X } from 'lucide-react';

type Props = {
  onConfirm: () => void;
  disabled?: boolean;
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
};

/**
 * 2-click delete button — first click enters "confirm" state (red pill),
 * second click within 4s triggers delete. Click outside cancels.
 * Reliable in all browsers (doesn't depend on window.confirm which Chrome can silently block).
 */
export function ConfirmDeleteButton({
  onConfirm,
  disabled,
  label = 'Xoá',
  size = 'sm',
  className = '',
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!confirming) return;
    timerRef.current = setTimeout(() => setConfirming(false), 4000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [confirming]);

  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const padding = size === 'sm' ? 'p-1.5' : 'p-2';

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    // Second click within 4s → actually delete
    setConfirming(false);
    onConfirm();
  };

  const handleCancel = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setConfirming(false);
  };

  if (confirming) {
    return (
      <div className={`inline-flex items-center gap-1 ${className}`}>
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={`inline-flex items-center gap-1 rounded-lg bg-rose-500 ${padding} px-2 text-xs font-semibold text-white transition hover:bg-rose-600 disabled:opacity-50`}
          title="Click lần nữa để xoá"
        >
          <Check className={iconSize} />
          {label} thật?
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className={`rounded-lg ${padding} text-slate-500 hover:bg-slate-200 dark:hover:bg-white/10`}
          title="Huỷ"
        >
          <X className={iconSize} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`rounded-lg ${padding} text-slate-400 transition hover:bg-rose-500/10 hover:text-rose-500 disabled:opacity-50 ${className}`}
      title={label}
      aria-label={label}
    >
      <Trash2 className={iconSize} />
    </button>
  );
}
